// src/components/nationalities/NationalitiesTable.jsx
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { Stack, useTheme } from "@mui/material";

import useGetNationalities from "../../hooks/nationalities/useGetNationalities";
import useDeleteNationalities from "../../hooks/nationalities/useDeleteNationalities";
import useDeleteAllNationalities from "../../hooks/nationalities/useDeleteAllNationalities";
import useRestoreNationality from "../../hooks/nationalities/useRestoreNationality";
import useRestoreAllNationalities from "../../hooks/nationalities/useRestoreAllNationalities";
import useForceDeleteNationality from "../../hooks/nationalities/useForceDeleteNationality";
import useForceDeleteAllNationalities from "../../hooks/nationalities/useForceDeleteAllNationalities";

import useTableActions from "../../hooks/common/useTableActions";
import TableLayout from "../global/Table/TableLayout";
import NationalitiesTableRow from "./Table/NationalitiesTableRow";
import NationalitiesTableColumns from "./Table/NationalitiesTableColumns";
import ArchiveButton from "../global/Table/ArchiveButton";
import toast from "react-hot-toast";
import { usePermissions } from "../../auth/PermissionsContext";

const NationalitiesTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";
    const { has } = usePermissions();

    const perms = useMemo(
        () => ({
            list: has("nationalities_list"),
            create: has("nationalities_create"),
            edit: has("nationalities_edit"),
            delete: has("nationalities_delete"),
            forceDelete: has("nationalities_force_delete"),
            archiveList: has("nationalities_archive_list"),
            restore: has("nationalities_restore"),
            restoreAll: has("nationalities_restore-all-table"),
            deleteAll: has("nationalities_delete-all-table"),
            massForceDeleteAll: has("nationalities_mass_force_delete-all-table"),
        }),
        [has]
    );

    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const isArchive = location.pathname.includes("/nationalities/archive");

    // CRUD Hooks
    const { deleteNationalities, isDelete } = useDeleteNationalities();
    const { deleteAllNationalities, isDeleteAll } = useDeleteAllNationalities();
    const { restoreNationality, isRestore } = useRestoreNationality();
    const { restoreAllNationalities, isRestoreAll } = useRestoreAllNationalities();
    const { forceDeleteNationality, isForceDelete } = useForceDeleteNationality();
    const { forceDeleteAllNationalities, isForceDeleteAll } = useForceDeleteAllNationalities();

    // ✅ استخدام Custom Hook للإجراءات المشتركة
    const {
        page,
        perPage,
        search,
        sortBy,
        sortDir,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleMenuRestore,
        handleBulkDelete,
        handlePageChange,
        handlePerPageChange,
        setSelectedIds,
    } = useTableActions({
        basePath: "/nationalities",
        deleteSingle: deleteNationalities,
        deleteAll: deleteAllNationalities,
        restoreSingle: restoreNationality,
        restoreAll: restoreAllNationalities,
        forceDeleteSingle: forceDeleteNationality,
        forceDeleteAll: forceDeleteAllNationalities,
        isArchive,
    });

    // ✨ Local State (specific to this table)
    const [statusFilter, setStatusFilter] = React.useState("");

    // ✨ Updated filters to include status and sorting
    const filters = useMemo(
        () => ({
            search: search || undefined,
            status: statusFilter || undefined,
            sort_by: sortBy,
            sort_dir: sortDir,
            ...(isArchive ? { only_trashed: true } : {}),
        }),
        [search, statusFilter, sortBy, sortDir, isArchive]
    );

    // ✨ Filter configuration for TableFilterBar
    const filterConfig = useMemo(
        () => [
            {
                type: "select",
                name: "status",
                label: { ar: "الحالة", en: "Status" },
                options: [
                    { value: "", label: { ar: "الكل", en: "All" } },
                    { value: "active", label: { ar: "نشط", en: "Active" } },
                    { value: "disabled", label: { ar: "معطل", en: "Disabled" } },
                ],
                value: statusFilter,
                onChange: setStatusFilter,
            },
        ],
        [statusFilter]
    );

    // ✨ Clear all filters handler
    const handleClearAllFilters = () => {
        setStatusFilter("");
    };

    const { isLoading, error, nationalities, meta } = useGetNationalities(
        page,
        perPage,
        filters
    );

    const items = useMemo(() => nationalities?.items || [], [nationalities]);

    const rowMenuDisabled = isArchive
        ? !perms.restore && !perms.forceDelete
        : !perms.edit && !perms.delete;

    const renderRow = (params) => (
        <NationalitiesTableRow
            key={params.item.id}
            nationality={params.item}
            index={params.index}
            isDark={isDark}
            page={params.page}
            perPage={params.perPage}
            sortBy={params.sortBy}
            sortDir={params.sortDir}
            totalItems={items.length}
            selectedIds={selectedIds}
            onRowCheckboxClick={handleRowCheckboxClick}
            onMenuOpen={handleMenuOpen}
            isArchive={isArchive}
            menuDisabled={rowMenuDisabled}
        />
    );

    const bulkDeleteDisabled = isArchive
        ? !perms.massForceDeleteAll
        : !perms.deleteAll;

    const title = isArabic ? "الجنسيات" : "Nationalities";
    const subtitle = isArchive
        ? isArabic
            ? "عرض الجنسيات المحذوفة (الأرشيف)"
            : "Archived nationalities (soft deleted)"
        : isArabic
            ? "إدارة الجنسيات المتاحة في النظام"
            : "Manage available nationalities in the system";

    const addButtonDisabled = isArchive
        ? !perms.restoreAll || selectedIds.length === 0
        : !perms.create;

    const addButtonLabel = isArchive
        ? isRestoreAll
            ? isArabic
                ? "جاري الاستعادة..."
                : "Restoring..."
            : isArabic
                ? "استعادة المحدد"
                : "Restore selected"
        : isArabic
            ? "إضافة جنسية"
            : "Create new nationality";

    const addButtonAction = isArchive
        ? () => {
            if (addButtonDisabled) {
                toast.error(
                    isArabic
                        ? "ليس لديك صلاحية للاستعادة"
                        : "No restore permission"
                );
                return;
            }

            if (isRestoreAll) return;

            if (!selectedIds.length) {
                toast.error(
                    isArabic
                        ? "الرجاء تحديد العناصر المراد استرجاعها أولاً"
                        : "Please select items to restore first"
                );
                return;
            }

            restoreAllNationalities({ items: selectedIds });
            setSelectedIds([]);
        }
        : () => {
            if (addButtonDisabled) {
                toast.error(
                    isArabic
                        ? "ليس لديك صلاحية للإنشاء"
                        : "No create permission"
                );
                return;
            }

            navigate("/nationalities/create");
        };

    return (
        <>
            {!isArchive && (
                <Stack
                    direction="row"
                    justifyContent={isArabic ? "flex-start" : "flex-end"}
                    sx={{ mb: 1 }}
                >
                    <ArchiveButton
                        to="/nationalities/archive"
                        visible={perms.archiveList}
                        disabled={!perms.archiveList}
                        archiveLabelAr="الأرشيف"
                        archiveLabelEn="Archive"
                    />
                </Stack>
            )}
            {isArchive && (
                <Stack
                    direction="row"
                    justifyContent={isArabic ? "flex-start" : "flex-end"}
                    sx={{ mb: 1 }}
                >
                    <ArchiveButton
                        to="/nationalities"
                        visible={true}
                        archiveLabelAr="العودة للقائمة"
                        archiveLabelEn="Back to list"
                    />
                </Stack>
            )}
            <TableLayout
                title={title}
                subtitle={subtitle}
                search={search}
                onSearchChange={setSearch}
                addButtonLabel={addButtonLabel}
                onAddClick={addButtonAction}
                addButtonVisible={true}
                isLoading={isLoading}
                error={error}
                items={items}
                meta={meta}
                page={page}
                perPage={perPage}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
                sortBy={sortBy}
                sortDir={sortDir}
                onRequestSort={handleRequestSort}
                columns={NationalitiesTableColumns}
                renderRow={renderRow}
                menuAnchorEl={menuAnchorEl}
                openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                onMenuDelete={handleMenuDelete}
                onMenuRestore={isArchive ? handleMenuRestore : undefined}
                onMenuEdit={!isArchive ? handleEdit : undefined}
                menuDisableEdit={!isArchive && !perms.edit}
                menuDisableRestore={isArchive && !perms.restore}
                menuDisableDelete={isArchive ? !perms.forceDelete : !perms.delete}
                isDeletingSingle={isArchive ? isRestore || isForceDelete : isDelete}
                selectedIds={selectedIds}
                onSelectAllClick={(e) => handleSelectAllClick(e, items)}
                onRowCheckboxClick={handleRowCheckboxClick}
                onBulkDelete={handleBulkDelete}
                bulkDeleteDisabled={bulkDeleteDisabled}
                isBulkDeleting={isArchive ? isForceDeleteAll : isDeleteAll}
                addButtonDisabled={addButtonDisabled}
                addButtonTooltip={
                    addButtonDisabled
                        ? isArabic
                            ? "لا تملك صلاحية تنفيذ هذا الإجراء"
                            : "You don't have permission"
                        : undefined
                }
                emptyLabelAr={
                    isArchive ? "لا توجد جنسيات في الأرشيف" : "لا توجد جنسيات"
                }
                emptyLabelEn={isArchive ? "No archived nationalities" : "No nationalities"}
                loadErrorAr="حدث خطأ أثناء تحميل الجنسيات"
                loadErrorEn="Failed to load nationalities"
                filters={filterConfig}
                onClearAllFilters={handleClearAllFilters}
            />
        </>
    );
};

export default NationalitiesTable;
